import React, { useMemo } from 'react';
import {
    Box, Typography, Stack, Avatar, Tooltip
} from '@mui/material';
import { Flight as FlightIcon } from '@mui/icons-material';

interface TravelFormData {
    id: number;
    staffName: string;
    email: string;
    projectNumber: string;
    projectName: string;
    client: string;
    travelDateFrom: string;
    travelDateTo: string;
    status: 'draft' | 'submitted' | 'approved';
    travelType?: 'project' | 'travel';
    travelReason?: string;
}

interface TravelTimelineProps {
    travelForms: TravelFormData[];
    currentDate: Date;
    viewMode: 'week' | 'month';
    onFormClick: (form: TravelFormData) => void;
    statusColors: Record<string, string>;
}

const TravelTimeline: React.FC<TravelTimelineProps> = ({
    travelForms,
    currentDate,
    viewMode,
    onFormClick,
    statusColors
}) => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const { startDate, endDate, days } = useMemo(() => {
        const start = new Date(currentDate);
        let end: Date;
        const daysList: Date[] = [];

        if (viewMode === 'week') {
            const dayOfWeek = start.getDay();
            const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
            start.setDate(start.getDate() - daysToSubtract);
            start.setHours(0, 0, 0, 0);
            end = new Date(start);
            end.setDate(start.getDate() + 6);
            end.setHours(23, 59, 59, 999);

            for (let i = 0; i < 7; i++) {
                const day = new Date(start);
                day.setDate(start.getDate() + i);
                daysList.push(day);
            }
        } else {
            start.setDate(1);
            start.setHours(0, 0, 0, 0);
            end = new Date(start);
            end.setMonth(start.getMonth() + 1);
            end.setDate(0);
            end.setHours(23, 59, 59, 999);

            const daysInMonth = end.getDate();
            for (let i = 0; i < daysInMonth; i++) {
                const day = new Date(start);
                day.setDate(start.getDate() + i);
                daysList.push(day);
            }
        }

        return { startDate: start, endDate: end, days: daysList };
    }, [currentDate, viewMode]);

    const relevantForms = useMemo(() => {
        return travelForms.filter(form => {
            const formStart = new Date(form.travelDateFrom);
            const formEnd = new Date(form.travelDateTo);
            formStart.setHours(0, 0, 0, 0);
            formEnd.setHours(23, 59, 59, 999);
            return formStart <= endDate && formEnd >= startDate;
        }).sort((a, b) => {
            const aStart = new Date(a.travelDateFrom).getTime();
            const bStart = new Date(b.travelDateFrom).getTime();
            return aStart - bStart;
        });
    }, [travelForms, startDate, endDate]);

    const getInitials = (name: string) => {
        if (!name) return '??';
        return name.split(' ').filter(n => n).map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
    };

    const formatDate = (date: Date) => {
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    };

    const isToday = (date: Date) => {
        const today = new Date();
        return date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
    };

    const calculateBarPosition = (form: TravelFormData) => {
        const formStart = new Date(form.travelDateFrom);
        const formEnd = new Date(form.travelDateTo);
        formStart.setHours(0, 0, 0, 0);
        formEnd.setHours(23, 59, 59, 999);

        const totalDays = days.length;
        const dayWidth = 100 / totalDays;

        const effectiveStart = formStart < startDate ? startDate : formStart;
        const effectiveEnd = formEnd > endDate ? endDate : formEnd;

        const startDayIndex = Math.floor((effectiveStart.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        const endDayIndex = Math.floor((effectiveEnd.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

        const left = startDayIndex * dayWidth;
        const width = (endDayIndex - startDayIndex + 1) * dayWidth;

        return {
            left: `${Math.max(0, left)}%`,
            width: `${Math.min(100 - left, width)}%`,
            startsBeforeView: formStart < startDate,
            endsAfterView: formEnd > endDate
        };
    };

    if (relevantForms.length === 0) {
        return (
            <Box sx={{ py: 6, textAlign: 'center' }}>
                <FlightIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                    No travel scheduled for this period
                </Typography>
                <Typography variant="body2" color="text.disabled">
                    Use the navigation to view other dates
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Timeline Header - Day columns */}
            <Box sx={{ display: 'flex', borderBottom: 2, borderColor: 'divider', pb: 1, mb: 2 }}>
                <Box sx={{ width: 180, flexShrink: 0, pr: 2 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        TRAVELLER
                    </Typography>
                </Box>
                <Box sx={{ flex: 1, display: 'flex' }}>
                    {days.map((day, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                flex: 1,
                                textAlign: 'center',
                                borderLeft: idx > 0 ? '1px solid' : 'none',
                                borderColor: 'divider',
                                bgcolor: isToday(day) ? 'primary.50' : 'transparent',
                                py: 0.5,
                                borderRadius: isToday(day) ? 1 : 0
                            }}
                        >
                            <Typography
                                variant="caption"
                                fontWeight={isToday(day) ? 'bold' : 'normal'}
                                color={isToday(day) ? 'primary.main' : 'text.secondary'}
                            >
                                {dayNames[day.getDay()]}
                            </Typography>
                            <Typography
                                variant="body2"
                                fontWeight={isToday(day) ? 'bold' : 'normal'}
                                color={isToday(day) ? 'primary.main' : 'text.primary'}
                            >
                                {day.getDate()}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Timeline Rows */}
            {relevantForms.map((form, formIdx) => {
                const barPos = calculateBarPosition(form);
                return (
                    <Box
                        key={form.id}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            py: 1.5,
                            borderBottom: formIdx < relevantForms.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                            '&:hover': { bgcolor: 'action.hover' },
                            transition: 'background-color 0.2s'
                        }}
                    >
                        {/* Traveller Info */}
                        <Box sx={{ width: 180, flexShrink: 0, pr: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Avatar
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        fontSize: '0.8rem',
                                        bgcolor: statusColors[form.status],
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => onFormClick(form)}
                                >
                                    {getInitials(form.staffName)}
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        noWrap
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => onFormClick(form)}
                                    >
                                        {form.staffName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {form.projectNumber}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        {/* Timeline Bar Area */}
                        <Box sx={{ flex: 1, position: 'relative', height: 40 }}>
                            {/* Grid lines */}
                            <Box sx={{ position: 'absolute', inset: 0, display: 'flex' }}>
                                {days.map((day, idx) => (
                                    <Box
                                        key={idx}
                                        sx={{
                                            flex: 1,
                                            borderLeft: idx > 0 ? '1px dashed' : 'none',
                                            borderColor: 'divider',
                                            bgcolor: isToday(day) ? 'primary.50' : 'transparent'
                                        }}
                                    />
                                ))}
                            </Box>

                            {/* Travel Bar */}
                            <Tooltip
                                title={
                                    <Box>
                                        <Typography variant="caption" fontWeight="bold" display="block">
                                            {form.staffName}
                                        </Typography>
                                        <Typography variant="caption" display="block">
                                            {form.projectNumber} • {form.client}
                                        </Typography>
                                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                            {formatDate(new Date(form.travelDateFrom))} — {formatDate(new Date(form.travelDateTo))}
                                        </Typography>
                                        <Typography variant="caption" display="block" sx={{ mt: 0.5, textTransform: 'capitalize' }}>
                                            Status: {form.status}
                                        </Typography>
                                    </Box>
                                }
                                arrow
                                placement="top"
                            >
                                <Box
                                    onClick={() => onFormClick(form)}
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        bottom: 4,
                                        left: barPos.left,
                                        width: barPos.width,
                                        bgcolor: statusColors[form.status],
                                        borderRadius: 1,
                                        borderTopLeftRadius: barPos.startsBeforeView ? 0 : 4,
                                        borderBottomLeftRadius: barPos.startsBeforeView ? 0 : 4,
                                        borderTopRightRadius: barPos.endsAfterView ? 0 : 4,
                                        borderBottomRightRadius: barPos.endsAfterView ? 0 : 4,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        px: 1,
                                        overflow: 'hidden',
                                        boxShadow: 1,
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            boxShadow: 3,
                                            transform: 'scaleY(1.1)'
                                        }
                                    }}
                                >
                                    <FlightIcon sx={{ fontSize: 14, color: 'white', mr: 0.5, flexShrink: 0 }} />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'white',
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}
                                    >
                                        {form.travelType === 'travel' 
                                            ? (form.travelReason || 'Travel')
                                            : (form.client ? `${form.client} - ${form.projectName || form.projectNumber}` : form.projectNumber)}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default TravelTimeline;
